import { create } from "zustand";
import { AxiosError } from "axios";
import { publicClient } from "@/api/publicClient";
import useAuthStore from "@/store/authStore";

interface UserData {
  first_name: string;
  last_name: string;
  email: string;
  title: string;
  password: string;
  password_confirmation: string;
  token: string;
}

interface InviteData {
  email: string;
  first_name: string;
  last_name: string;
  role_type: string;
  is_existing_user: boolean;
  agency_name?: string;
}

interface OnboardingState {
  userData: Partial<UserData>;
  inviteData: InviteData | null;
  sendUserData: (data: Partial<UserData>) => Promise<void>;
  validateToken: (token: string) => Promise<InviteData | false>;
  acceptExistingUser: (token: string) => Promise<{ email: string; agency_id: string }>;
}

const useMediaUserOnboardingStore = create<OnboardingState>((set) => ({
  userData: {},
  inviteData: null,

  // Uses publicClient — user is accepting invite, no auth token yet
  sendUserData: async (data: UserData) => {
    try {
      const response = await publicClient.post(
        `/media-agency-invitations/accept/${data.token}`,
        data
      );

      // The accept endpoint can return two shapes:
      //  - First-time success: { token, user, ... }  → finish onboarding
      //  - Idempotent already-a-member: { already_member: true, agency_id }
      //                                  → no token issued; user must log in
      // Persisting an `undefined` token here would corrupt localStorage and
      // then 401 every subsequent request, so reject the no-token path.
      if (!response.data?.token || !response.data?.user) {
        const message =
          response.data?.message ||
          "You're already a member of this agency. Please log in to continue.";
        throw new Error(message);
      }

      const auth = useAuthStore.getState();
      // Clear any leftover active agency from a prior session in this browser
      // before binding the new auth payload. If the invitee was already in
      // another agency, the post-accept follow-up to /agency/select will
      // re-establish a valid active agency; we never want to send the
      // previous user's X-Agency-Id with the new user's bearer token.
      auth.setActiveAgency(null);
      auth.setAuth(
        response.data.user,
        response.data.token,
        null,
        null,
        null,
        null
      );
      set({ userData: response.data });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : (error instanceof Error ? error.message : "Something went wrong");
      throw new Error(errorMessage);
    }
  },

  // Uses publicClient — validates token and returns invite data for pre-filling
  validateToken: async (token) => {
    try {
      const res = await publicClient.get(
        `/media-agency-invitations/token/${token}`
      );
      const data = res.data.data as InviteData;
      set({ inviteData: data });
      return data;
    } catch {
      return false;
    }
  },

  // Accept invite for existing users - no password required
  acceptExistingUser: async (token: string) => {
    try {
      const response = await publicClient.post(
        `/media-agency-invitations/accept-existing/${token}`
      );
      return {
        email: response.data.user_email,
        agency_id: response.data.agency_id,
      };
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : (error instanceof Error ? error.message : "Something went wrong");
      throw new Error(errorMessage);
    }
  },
}));

export default useMediaUserOnboardingStore;
